import { useNavigate } from "react-router-dom";
import moment from "moment";
import { maskMiddleCharacters } from "util/function/function";

const C = {
  green: "#059669",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  greenBorder: "#A7F3D0",
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  amber800: "#92400E",
  amber100: "#FEF3C7",
};

function StatusChip({ status }) {
  const map = {
    0: { label: "매칭중", bg: C.amber100, fg: C.amber800, dot: "#F59E0B" },
    1: { label: "매칭완료", bg: "#DBEAFE", fg: "#1E40AF", dot: "#3B82F6" },
    2: { label: "완료", bg: C.greenBg, fg: C.greenDark, dot: C.green },
  };
  const s = map[String(status)] ?? map[0];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 12px",
        borderRadius: 9999,
        background: s.bg,
        color: s.fg,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 9999, background: s.dot, flexShrink: 0 }} />
      {s.label}
    </span>
  );
}

const categoryEmoji = (name = "") => {
  const s = String(name);
  if (s.includes("트랙터")) return "🚜";
  if (s.includes("콤바인")) return "🌾";
  if (s.includes("제어기") || s.includes("전자") || s.includes("전기")) return "⚡";
  if (s.includes("엔진") || s.includes("모터")) return "⚙️";
  if (s.includes("배터리")) return "🔋";
  if (s.includes("오일") || s.includes("연료")) return "🛢️";
  if (s.includes("이앙기") || s.includes("파종")) return "🌱";
  if (s.includes("바퀴") || s.includes("타이어")) return "⚫";
  return "🔧";
};

const relativeTime = (dateStr) => {
  if (!dateStr) return "";
  const m = moment(dateStr);
  if (!m.isValid()) return "";
  const diffMin = moment().diff(m, "minutes");
  if (diffMin < 1) return "방금 전";
  if (diffMin < 60) return `${diffMin}분 전`;
  const diffHr = moment().diff(m, "hours");
  if (diffHr < 24) return `${diffHr}시간 전`;
  const diffDay = moment().diff(m, "days");
  if (diffDay < 7) return `${diffDay}일 전`;
  return m.format("YYYY.MM.DD");
};

// 주소 첫 7글자만 노출 (개인정보 보호)
const maskAddress = (address) => {
  if (!address || typeof address !== "string") return "";
  return address.length > 7 ? `${address.substring(0, 7)} ⋯` : address;
};

const ReceivedMatchingItem = ({ item }) => {
  const navigate = useNavigate();
  const names = item?.categoryListName ?? [];
  const isEtc = names[0] === "기타";
  const primary = isEtc ? "기타" : names[0] || "기타";
  const secondary = isEtc ? "" : [names[1], names[2]].filter(Boolean).join(" · ");
  const emoji = categoryEmoji(primary);
  const images = Array.isArray(item?.imageUrls) ? item.imageUrls : [];
  const firstImage = images[0];
  const photoCount = images.length;

  return (
    <div
      onClick={() => navigate(`${item.docId}`)}
      style={{
        display: "flex",
        gap: 16,
        padding: 20,
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 14,
        cursor: "pointer",
        transition: "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 8px 20px rgba(16, 185, 129, 0.08)";
        e.currentTarget.style.borderColor = C.greenBorder;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.borderColor = C.line;
      }}
    >
      {/* Thumbnail */}
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: 12,
          background: firstImage ? "#F3F4F6" : C.greenBg,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 32,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {firstImage ? (
          <img src={firstImage} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <span>{emoji}</span>
        )}
        {photoCount > 1 && (
          <span
            style={{
              position: "absolute",
              bottom: 4,
              right: 4,
              background: "rgba(17,24,39,0.75)",
              color: "#fff",
              fontSize: 10,
              fontWeight: 700,
              padding: "2px 6px",
              borderRadius: 6,
            }}
          >
            +{photoCount - 1}
          </span>
        )}
      </div>

      {/* Body */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: C.ink,
              letterSpacing: "-0.01em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
              minWidth: 0,
            }}
          >
            {primary}
            {secondary && <span style={{ color: C.sub, fontWeight: 500 }}> · {secondary}</span>}
          </div>
          <StatusChip status={item?.status} />
        </div>

        {item?.etc && (
          <div
            style={{
              fontSize: 13,
              color: C.body,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.etc}
          </div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontSize: 12,
            color: C.sub,
            flexWrap: "wrap",
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span aria-hidden>👤</span> {maskMiddleCharacters(item?.name) || "익명"}
          </span>
          <span style={{ width: 2, height: 2, background: "#D1D5DB", borderRadius: 9999 }} />
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span aria-hidden>📍</span> {maskAddress(item?.address)}
          </span>
          <span style={{ width: 2, height: 2, background: "#D1D5DB", borderRadius: 9999 }} />
          <span>{relativeTime(item?.createDate)}</span>
          {photoCount > 0 && (
            <>
              <span style={{ width: 2, height: 2, background: "#D1D5DB", borderRadius: 9999 }} />
              <span>사진 {photoCount}장</span>
            </>
          )}
        </div>

        <div
          style={{
            marginTop: 4,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: C.greenDark,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            상세보기 →
          </span>
        </div>
      </div>
    </div>
  );
};

export default ReceivedMatchingItem;
